// src/components/operationCenter/widgets/shared/CompactTokenSelector.tsx
// Compact inline token selector for dashboard widgets
// Styled to match token screener select dropdowns

import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Search } from "lucide-react";

interface CompactTokenSelectorProps {
  /** Current selected ticker(s) */
  value: string | string[];
  /** Callback when selection changes */
  onChange: (value: string | string[]) => void;
  /** Allow multiple selection */
  multiple?: boolean;
  /** Available tokens */
  tokens?: string[];
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

// Common crypto tickers for quick selection
const DEFAULT_TOKENS = [
  "BTC", "ETH", "SOL", "BNB", "XRP", "ADA", "AVAX", "DOT", "MATIC", "LINK",
  "UNI", "ATOM", "LTC", "BCH", "NEAR", "APT", "ARB", "OP", "DOGE", "SHIB"
];

const CompactTokenSelector: React.FC<CompactTokenSelectorProps> = ({
  value,
  onChange,
  multiple = false,
  tokens = DEFAULT_TOKENS,
  placeholder = "Select token",
  disabled = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Normalize value to array for internal handling
  const selectedTokens = Array.isArray(value) ? value : value ? [value] : [];

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredTokens = tokens.filter(t => 
    t.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (token: string) => {
    if (multiple) {
      const newSelection = selectedTokens.includes(token)
        ? selectedTokens.filter(t => t !== token)
        : [...selectedTokens, token];
      onChange(newSelection);
    } else {
      onChange(token);
      setIsOpen(false);
      setSearch("");
    }
  };

  const displayValue = multiple
    ? selectedTokens.length > 0 
      ? selectedTokens.length <= 2 
        ? selectedTokens.join(", ")
        : `${selectedTokens.slice(0, 2).join(", ")} +${selectedTokens.length - 2}`
      : placeholder
    : selectedTokens[0] || placeholder;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Button styled like DaisyUI select select-sm */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center justify-between gap-2 h-8 px-3 rounded-lg text-xs font-medium
          bg-gray-800/60 border border-gray-700/50 transition-colors min-w-[100px]
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-600 cursor-pointer"}
          ${isOpen ? "border-blue-500/50" : ""}
        `}
      >
        <span className={selectedTokens.length > 0 ? "text-slate-200" : "text-slate-400"}>
          {displayValue}
        </span>
        <ChevronDown 
          className={`w-3.5 h-3.5 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl min-w-[180px] max-w-[220px]">
          {/* Search */}
          <div className="p-2 border-b border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-900/80 border border-gray-600 rounded-md text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                autoFocus
              />
            </div>
          </div>

          {/* Token list */}
          <div className="max-h-[200px] overflow-y-auto p-1.5">
            {filteredTokens.length === 0 ? (
              <div className="px-3 py-2 text-xs text-slate-500">No tokens found</div>
            ) : (
              filteredTokens.map(token => {
                const isSelected = selectedTokens.includes(token);
                return (
                  <button
                    key={token}
                    type="button"
                    onClick={() => handleSelect(token)}
                    className={`
                      w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left text-xs transition-colors
                      ${isSelected 
                        ? "bg-blue-500/20 text-blue-300" 
                        : "text-slate-300 hover:bg-gray-700/60"
                      }
                    `}
                  >
                    <span className="font-medium">{token}</span>
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Multiple selection hint */}
          {multiple && selectedTokens.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-700 text-[11px] text-slate-500">
              {selectedTokens.length} selected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompactTokenSelector;
